import { useApolloClient } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { BbplMonitor, Util } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  GetBbplMonitorInstancesQueryGQLType,
  GetBbplMonitorUploadUploadSignedUrlDocumentGQLType,
  useGetBbplMonitorDocumentQuery,
  useGetBbplMonitorInstancesQuery,
} from '../requests/bbplMonitor.gql.generated';
import {
  BBPLGraphState,
  BBPLMonitorParams,
  BBPLStatus,
  RDFV,
} from '../types/bbplMonitorTypes';
import { useStatus } from './bbplStatusContext';
import { useDocumentVisibility } from './useDocumentVisibility';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { presignedPostUpload } from 'packages/files/ui/utils';

const isLatestPipeline = ({
  status,
  current,
}: {
  status: BBPLStatus;
  current: RDFV;
}) => {
  const { mostRecentRDFV } = status;
  // there was no latest version (that is pipeline never ran)
  if (!mostRecentRDFV) {
    return true;
  }

  // most recent pipeline date is newer than the current pipeline
  if (mostRecentRDFV.reportingDate > current.reportingDate) {
    return false;
  }

  // most recent pipeline date is same as current pipeline but version is newer
  if (
    mostRecentRDFV.reportingDate === current.reportingDate &&
    mostRecentRDFV.fileVersion > current.fileVersion
  ) {
    return false;
  }

  return true;
};

type BBPLMonitorData = {
  instancesData: GetBbplMonitorInstancesQueryGQLType | undefined;
  graphData: BBPLGraphState | undefined;
  directUpload: (params: {
    file: File;
    artifactId: string;
    onEventCallback: UploadCallback;
  }) => Promise<string>;
};

const useBBPLMonitorData = (params: BBPLMonitorParams): BBPLMonitorData => {
  const toast = useToast();
  const { reportingDate, fileVersion } = params;
  const client = useApolloClient();
  const { status } = useStatus();
  const { borrowerOrganizationId, creditFacilityId } = status;

  const isDocumentVisible = useDocumentVisibility();
  const shouldPollInstances =
    isDocumentVisible && isLatestPipeline({ status, current: params });

  const { data: instancesData } = useGetBbplMonitorInstancesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      if (!commonUtils.EnvUtils.isProduction()) {
        errorToastConfig.title = err.message;
        toast(errorToastConfig);
      }
    },
    variables: {
      reportingDate,
      fileVersion,
      creditFacilityId,
    },
    pollInterval: shouldPollInstances ? 5000 : 0,
    fetchPolicy: 'network-only', // Can change as a pipeline run progresses.
  });

  const {
    data: documentData,
    variables: documentParams,
  } = useGetBbplMonitorDocumentQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      if (!commonUtils.EnvUtils.isProduction()) {
        errorToastConfig.title = err.message;
        toast(errorToastConfig);
      }
    },
    variables: {
      artifactDocumentId:
        instancesData?.bbplMonitorInstanceRecords.artifactDocumentId ??
        status.mainArtifactDocumentId,
      creditFacilityId,
    },
  });
  const artifactDocumentId = documentParams?.artifactDocumentId;

  const graphData = useMemo<BBPLGraphState | undefined>(() => {
    const document = documentData
      ? DbGraphUtils.deserializeForBBPLMonitor(
          documentData.bbplMonitorArtifactDocumentInfo.artifactsDocumentString,
        )
      : null;

    const artifactIdToInstanceMap = new Map<string, BbplMonitor.Instance>(
      instancesData?.bbplMonitorInstanceRecords.artifactInstances.map(
        (gqlInstance) => {
          const parsedInstance = JSON.parse(gqlInstance.instanceRecordString);
          const instance: BbplMonitor.Instance = {
            id: gqlInstance.id,
            artifactId: parsedInstance.artifactId,
            status: parsedInstance.status,
            updated_at: new Date(parsedInstance.updated_at),
            created_at: new Date(parsedInstance.created_at),
            statusDetails: parsedInstance.statusDetails,
          };
          return [instance.artifactId, instance];
        },
      ),
    );
    const artifacts = document?.artifacts ?? [];
    const edges = document?.edges ?? [];
    const nodes = artifacts.map((artifact) => ({
      artifact,
      instance: artifactIdToInstanceMap.get(artifact.id) || null,
    }));

    return {
      currentArtifactDocumentId: artifactDocumentId ?? null,
      currentRDFV: {
        reportingDate,
        fileVersion,
      },
      nodes,
      edges,
    };
  }, [
    artifactDocumentId,
    documentData,
    instancesData,
    fileVersion,
    reportingDate,
  ]);

  // The helper throws and captures a sentry exception if an error occurs
  const directUpload = useCallback(
    async ({
      file,
      artifactId,
      onEventCallback,
    }: {
      file: File;
      artifactId: string;
      onEventCallback: UploadCallback;
    }): Promise<string> => {
      // The helper throws and captures a sentry exception if an error occurs
      const { queryResponse } = await presignedPostUpload<
        BbplMonitor.UploadDestinationRequest,
        { artifactInstanceId: string }
      >({
        client,
        file,
        query: GetBbplMonitorUploadUploadSignedUrlDocumentGQLType,
        queryVariables: {
          organizationId: borrowerOrganizationId,
          artifactId,
          reportingDate,
          fileVersion,
        },
        progressEventCallback: ({ loaded, total }) => {
          onEventCallback(loaded, total);
        },
      });

      finleyAssert(
        queryResponse.artifactInstanceId,
        'BBPL artifact instance id missing.',
      );

      return queryResponse.artifactInstanceId;
    },
    [client, fileVersion, borrowerOrganizationId, reportingDate],
  );

  return useMemo(() => ({ instancesData, graphData, directUpload }), [
    instancesData,
    graphData,
    directUpload,
  ]);
};

export type BBPLMonitorContext = {
  state: BBPLGraphState;
  selectedNodeId: string | null;
  setSelectedNode: (nodeId: string | null) => void;
  directUpload: (params: {
    file: File;
    artifactId: string;
    onEventCallback: UploadCallback;
  }) => Promise<string>;
};

const bbplMonitorContext = createContext<BBPLMonitorContext | null>(null);

export const useGraph = (): BBPLMonitorContext => {
  const context = useContext(bbplMonitorContext);
  finleyAssert(
    context,
    'useBbplMonitorContext must be used within a BbplMonitorContextProvider.',
  );

  return context;
};

export type UploadCallback = (loaded: number, total: number) => void;
export type UploadFunction = (
  file: File,
  onEventCallback: UploadCallback,
) => Promise<void>;

// describes the upload details for a node.
export type UploadDetails =
  | {
      type: 'file';
      upload: UploadFunction;
      extension: Util.SupportedFileExtension;
      message: null;
    }
  | {
      type: 'cashBalance';
      creditFacilityId: string;
      organizationId: string;
      reportingDate: Util.FullDateStr;
      fileVersion: number;
    }
  | {
      type: 'notAllowed';
      shortMessage: string;
      message: string; // when upload is not supported, this message will be displayed in a dialog.
    }
  | {
      type: 'alreadyUploaded';
    }
  | {
      type: 'notConfigured';
    };

export type DisplayNode = {
  displayId: string;
  hasInputs: boolean;
  hasOutputs: boolean;
  artifact: BbplMonitor.Artifact;
  instance: BbplMonitor.Instance | null;
  uploadDetails: UploadDetails;
  isSelected: boolean;
};

export const useNode = (nodeId: string): DisplayNode | null => {
  const { status } = useStatus();
  const { state, directUpload, selectedNodeId } = useGraph();
  const node = state.nodes.find(({ artifact }) => artifact.id === nodeId);
  if (!node) {
    return null;
  }

  const hasInputs = state.edges.some((edge) => edge.target === nodeId);
  const hasOutputs = state.edges.some((edge) => edge.source === nodeId);

  // we can upload tapes if
  // 1. node is configured with uploadExtension
  // 2. node does not already have an instance
  // 3. current date is greater than or equal to most recent RDFV
  // 4. graph is most recent. That means currentArtifactDocumentId is null or currentArtifactDocumentId === mainArtifactDocumentId

  // this function checks if a file can be uploaded.
  // returns true if all conditions are met.
  // retruns a string representing a message that explains why the file cannot be uploaded.

  const canUpload = (): UploadDetails => {
    // for anything but cashBalancesFeedContext, we need file extension configured
    // to be able to support uploads. If its missing that means the node
    // is not configured for uploads.
    if (
      node.artifact.type !== 'cashBalancesFeedContext' &&
      !node.artifact.uploadExtension
    ) {
      return {
        type: 'notConfigured',
      };
    }

    // for cash balance feed nodes check the uploadCashForm
    if (
      node.artifact.type === 'cashBalancesFeedContext' &&
      !node.artifact.uploadCashForm
    ) {
      return {
        type: 'notConfigured',
      };
    }

    if (node.instance) {
      return {
        type: 'alreadyUploaded',
      };
    }

    if (
      status.mostRecentRDFV &&
      status.mostRecentRDFV.reportingDate > state.currentRDFV.reportingDate
    ) {
      // @bbplMonitor: support historical pipleline
      // when supporing historical pipeline, we need to query the RDFV for given date
      // and ensure that new version is greater than older.
      return {
        type: 'notAllowed',
        shortMessage: 'Old Date',
        message: `This is an old (${state.currentRDFV.reportingDate}) pipeline. Uploading for historical is not supported yet. Please select current (${status.mostRecentRDFV.reportingDate}) or newer`,
      };
    }

    if (
      status.mostRecentRDFV &&
      status.mostRecentRDFV.reportingDate === state.currentRDFV.reportingDate &&
      status.mostRecentRDFV.fileVersion > state.currentRDFV.fileVersion
    ) {
      return {
        type: 'notAllowed',
        shortMessage: 'Old Version',
        message: `This is an old (v${state.currentRDFV.fileVersion}) pipeline. Please upload for current (v${status.mostRecentRDFV.fileVersion}) or newer`,
      };
    }

    if (
      state.currentArtifactDocumentId &&
      state.currentArtifactDocumentId !== status.mainArtifactDocumentId
    ) {
      return {
        type: 'notAllowed',
        shortMessage: 'Graph Changed',
        message: 'Graph has changed. Please upload tapes with new version.',
      };
    }

    // for cash balance feed context, we need to upload cash balance.
    if (node.artifact.type === 'cashBalancesFeedContext') {
      // @bbplMonitor: we need to figure out if we should be
      // acccepting cash balance in this context or raw loan tape node
      // this should probably come from config on raw cash balance node.
      return {
        type: 'cashBalance',
        creditFacilityId: status.creditFacilityId,
        organizationId: status.borrowerOrganizationId,
        reportingDate: state.currentRDFV.reportingDate,
        fileVersion: state.currentRDFV.fileVersion,
      };
    }

    // at this point we must have uploadExtension
    finleyAssert(node.artifact.uploadExtension, 'Upload extension missing');
    // otherwise import file.
    return {
      type: 'file',
      upload: async (file: File, onEventCallback: UploadCallback) => {
        await directUpload({
          file,
          artifactId: node.artifact.id,
          onEventCallback,
        });
      },
      extension: node.artifact.uploadExtension,
      message: null,
    };
  };

  const uploadDetails = canUpload();

  // generate a node displayId for the children to help manage state.
  const displayId =
    node.artifact.id +
    state.currentRDFV.reportingDate +
    state.currentRDFV.fileVersion;
  return {
    displayId,
    hasInputs,
    hasOutputs,
    artifact: node.artifact,
    instance: node.instance,
    uploadDetails,
    isSelected: selectedNodeId === node.artifact.id,
  };
};

export const GraphProvider = ({
  children,
  params,
}: {
  children: React.ReactNode;
  params: BBPLMonitorParams;
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const { graphData, directUpload } = useBBPLMonitorData(params);

  const value = useMemo<BBPLMonitorContext | null>(() => {
    if (!graphData) return null;
    return {
      state: graphData,
      selectedNodeId,
      setSelectedNode: setSelectedNodeId,
      directUpload,
    };
  }, [graphData, directUpload, selectedNodeId, setSelectedNodeId]);

  return (
    <bbplMonitorContext.Provider value={value}>
      {value && children}
    </bbplMonitorContext.Provider>
  );
};
